/**
 * Hospitality page templates.
 *
 * Each template is a ready-to-use Puck `Data` snapshot a new page can be
 * seeded with. Components are referenced by their Puck registry keys
 * (see `puck-config.tsx`). We only override what makes the template feel
 * distinct — every other prop falls back to the component's hospitality
 * defaults so customers get tasteful copy out of the box.
 *
 * Stable per-node ids are filled in server-side by `ensurePuckNodeIds`
 * (in `apps/dashboard/src/api/pages.ts`), so templates can omit them.
 */

export type PuckTemplateNode = {
  type: string;
  props: Record<string, unknown>;
};

export type PuckTemplateData = {
  content: PuckTemplateNode[];
  root: { props: Record<string, unknown> };
  zones?: Record<string, PuckTemplateNode[]>;
};

export type PuckTemplate = {
  id: string;
  label: string;
  description: string;
  suggestedSlug: string;
  suggestedTitle: string;
  puckData: PuckTemplateData;
};

// ─── 1. Boutique lodge — Homepage ───────────────────────────────────────────

const boutiqueLodgeHome: PuckTemplate = {
  id: "boutique-lodge-home",
  label: "Boutique lodge — Homepage",
  description:
    "Hero photo, rooms, amenities, gallery, host story, offers, inquiry. Built for a multi-room lodge.",
  suggestedSlug: "home",
  suggestedTitle: "Home",
  puckData: {
    root: {
      props: {
        title: "Pine Hollow Lodge — Home",
        fontFamily: "Inter",
        backgroundColor: "#ffffff",
      },
    },
    zones: {},
    content: [
      {
        type: "Navbar",
        props: {
          id: "Navbar-ff09a8f0-72d0-4ae2-add1-ffdb61a0ef5d",
          links: [
            {
              href: "/stay",
              label: "Stay",
              hasDropdown: true,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [
                {
                  links: [
                    {
                      href: "/rooms-garden-suite",
                      icon: "MatrialIconsHotel",
                      label: "Rooms & Suites",
                      description: "Browse our individually styled rooms.",
                      subSections: [],
                    },
                    {
                      href: "/cabins",
                      icon: "MatrialIconsApartment",
                      label: "Cabins & Villas",
                      description: "Private cabins for couples and families.",
                      subSections: [],
                    },
                    {
                      href: "/property",
                      icon: "FluentTent28Filled",
                      label: "The Property",
                      description: "Grounds, gardens, and shared spaces.",
                      subSections: [],
                    },
                  ],
                  title: "",
                },
              ],
            },
            {
              href: "/amenities",
              label: "Amenities",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/gallery",
              label: "Gallery",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/local-guide",
              label: "Local Guide",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/offers",
              label: "Offers",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/faq",
              label: "FAQ",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/contact",
              label: "Contact",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
          ],
          style: "blur",
          shadow: "none",
          ctaHref: "/contact",
          linkGap: 8,
          ctaLabel: "Send Inquiry",
          logoHref: "/",
          logoText: "Pine Hollow Lodge",
          logoType: "text",
          maxWidth: "2xl",
          paddingX: "md",
          paddingY: "none",
          textColor: "#111827",
          ctaVariant: "solid",
          ctaFontSize: 14,
          ctaPaddingX: 16,
          ctaPaddingY: 9,
          linkOpacity: 80,
          linksCtaGap: 8,
          backdropBlur: 10,
          borderBottom: true,
          ctaTextColor: "#FFFFFF",
          dividerColor: "",
          linkFontSize: 14,
          logoFontSize: 17,
          logoImageAlt: "Lodge logo",
          logoImageUrl: "",
          logoLinksGap: 33,
          navbarHeight: 64,
          positionType: "sticky",
          linkTextColor: "#111827",
          ctaBorderColor: "#111827",
          linkFontWeight: "medium",
          linksAlignment: "center",
          logoFontWeight: "semibold",
          logoImageWidth: 0,
          backgroundColor: "#FFFFFF",
          ctaBorderRadius: "full",
          ctaOpenInNewTab: false,
          logoImageHeight: 28,
          mobileMenuStyle: "fullscreen-right",
          logoImageBgColor: "transparent",
          logoImagePadding: 0,
          backgroundOpacity: 80,
          borderBottomColor: "#e5e7eb",
          borderBottomWidth: 1,
          ctaHoverTextColor: "#FFFFFF",
          ctaBackgroundColor: "#111827",
          ctaHoverBorderColor: "#1f2937",
          logoImageBorderRadius: "none",
          ctaHoverBackgroundColor: "#1f2937",
        },
      },
      {
        type: "Hero",
        props: {
          id: "Hero-6fad01e9-8823-4a86-9fea-93f929cfd921",
          preset: "custom",
          actions: [
            {
              type: "Button",
              props: {
                id: "Button-911926b3-7d1c-4363-ad09-ffe5db305bd5",
                url: "/pricing",
                icon: "none",
                size: "lg",
                label: "Check Availability",
                shadow: "none",
                rounded: "full",
                variant: "primary",
                fullWidth: true,
                marginTop: 0,
                textColor: "",
                marginLeft: 0,
                marginRight: 90,
                iconPosition: "right",
                marginBottom: 0,
                openInNewTab: false,
                customPaddingX: 22,
                customPaddingY: 9,
                hoverTextColor: "",
                backgroundColor: "#0930f6",
                hoverBackgroundColor: "",
              },
            },
          ],
          heading: "Your quiet escape, framed by pine and lake",
          alignment: "center",
          minHeight: "lg",
          textColor: "#ffffff",
          subheading:
            "Six light-filled rooms, two cabins, and breakfast on the porch. Hosted in person by Anna and Theo.",
          showActions: "show",
          headingColor: "",
          headingPrefix: "",
          headingSuffix: "",
          headingWeight: "extrabold",
          overlayOpacity: 50,
          backgroundColor: "#0d1526",
          backgroundImage: "",
          headingFontSize: 0,
          headingMaxWidth: 800,
          highlightColors: "#3B82F6,#8B5CF6,#EC4899,#F59E0B",
          subheadingColor: "",
          headingHighlight: "",
          subheadingWeight: "normal",
          subheadingOpacity: 90,
          highlightAnimation: "none",
          subheadingFontSize: 0,
          subheadingMaxWidth: 850,
          showHighlightUnderline: false,
          highlightUnderlineColor: "rgba(147,197,253,0.7)",
        },
      },
      {
        type: "AccommodationGrid",
        props: {
          id: "AccommodationGrid-ec1cdbe7-a624-4ba1-8589-d68a6b4bd454",
          items: [
            {
              size: "32 m²",
              price: "€180",
              title: "Garden Suite",
              ctaUrl: "/rooms/garden-suite",
              bedType: "1 King bed",
              eyebrow: "",
              capacity: "Sleeps 2",
              ctaLabel: "View room",
              imageAlt: "Garden Suite",
              imageUrl:
                "https://placehold.co/1200x800/efeae2/8b6f4e?text=Garden+Suite",
              amenities: [
                { text: "Garden view" },
                { text: "Breakfast included" },
                { text: "Free Wi-Fi" },
              ],
              description:
                "A private suite opening onto the walled garden, with a king bed and deep soaking tub.",
              priceSuffix: "/ night",
              priceFromLabel: "From",
              ctaOpenInNewTab: false,
            },
            {
              size: "58 m²",
              price: "€260",
              title: "Pine Cabin",
              ctaUrl: "/cabins/pine",
              bedType: "1 King + 2 Singles",
              eyebrow: "Family favourite",
              capacity: "Sleeps 4",
              ctaLabel: "View cabin",
              imageAlt: "Pine Cabin",
              imageUrl:
                "https://placehold.co/1200x800/dde7d6/4b6043?text=Pine+Cabin",
              amenities: [
                { text: "Wood stove" },
                { text: "Private deck" },
                { text: "Kitchenette" },
              ],
              description:
                "A two-bedroom cabin tucked under the pines, with a wood-burning stove and private deck.",
              priceSuffix: "/ night",
              priceFromLabel: "From",
              ctaOpenInNewTab: false,
            },
            {
              size: "38 m²",
              price: "€210",
              title: "Olive Loft",
              ctaUrl: "/rooms/olive-loft",
              bedType: "1 Queen bed",
              eyebrow: "",
              capacity: "Sleeps 2",
              ctaLabel: "View loft",
              imageAlt: "Olive Loft",
              imageUrl:
                "https://placehold.co/1200x800/e7dccb/6a4b1f?text=Olive+Loft",
              amenities: [
                { text: "Courtyard view" },
                { text: "Espresso machine" },
                { text: "Rain shower" },
              ],
              description:
                "An open-plan loft above the courtyard with vaulted ceilings and treetop views.",
              priceSuffix: "/ night",
              priceFromLabel: "From",
              ctaOpenInNewTab: false,
            },
          ],
          columns: "3",
          eyebrow: "STAY WITH US",
          heading: "Choose your room or cabin",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          alignment: "left",
          textColor: "#0f172a",
          cardShadow: "md",
          subheading:
            "Twelve individually designed spaces, from intimate garden rooms to family cabins under the pines.",
          cardRounded: "2xl",
          cardVariant: "editorial",
          footerCtaUrl: "/contact",
          cardTextColor: "#0f172a",
          showFooterCta: true,
          footerCtaLabel: "Send an inquiry about availability",
          backgroundColor: "#fafaf9",
          cardAccentColor: "#0f172a",
          cardBackgroundColor: "#ffffff",
          footerCtaOpenInNewTab: false,
        },
      },
      {
        type: "AmenitiesGrid",
        props: {
          id: "AmenitiesGrid-0ee7d1ff-3506-4ac0-9be2-f41bf2f0b8df",
          items: [
            {
              icon: "breakfast",
              title: "Sourdough breakfast",
              description:
                "Slow breakfast each morning with sourdough, garden fruit, and local cheeses.",
            },
            {
              icon: "pool",
              title: "Wood-fired sauna",
              description:
                "Heated each evening, with a cold plunge under the pines.",
            },
            {
              icon: "coffee",
              title: "Honesty bar",
              description:
                "Local wines, amari, and craft beers from the village down the hill.",
            },
            {
              icon: "wifi",
              title: "Fast Wi-Fi everywhere",
              description:
                "Full-property mesh — works just as well on the terrace.",
            },
            {
              icon: "parking",
              title: "Free on-site parking",
              description:
                "Shaded spaces beside your room — no need to move the car.",
            },
            {
              icon: "pet",
              title: "Pet-friendly cabins",
              description: "Two cabins welcome dogs at no extra charge.",
            },
            {
              icon: "local",
              title: "Local experiences desk",
              description:
                "We'll book the boat trip, the truffle hunt, or just point you toward the right bakery.",
            },
            {
              icon: "fire",
              title: "Evening fire pit",
              description: "Logs and blankets out by the olive grove from 8pm.",
            },
          ],
          layout: "card",
          columns: "4",
          eyebrow: "What's included",
          heading: "Comforts you'll actually use",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          alignment: "left",
          iconColor: "#6b5b3e",
          textColor: "#0f172a",
          subheading:
            "Everything is included in your stay — no resort fees, no surprises.",
          accentColor: "#6b5b3e",
          backgroundColor: "#ffffff",
        },
      },
      {
        type: "GallerySection",
        props: {
          id: "GallerySection-6d3aeb22-7630-4b94-ba87-c0373caa0598",
          gap: "md",
          ctaUrl: "/gallery",
          images: [
            {
              caption: "The Garden Suite at first light",
              imageAlt: "Garden Suite interior",
              imageUrl:
                "https://placehold.co/1600x1200/efeae2/8b6f4e?text=Garden+Suite",
            },
            {
              caption: "",
              imageAlt: "Pine Cabin exterior",
              imageUrl:
                "https://placehold.co/900x900/dde7d6/4b6043?text=Pine+Cabin",
            },
            {
              caption: "",
              imageAlt: "Breakfast on the terrace",
              imageUrl:
                "https://placehold.co/900x900/e7dccb/6a4b1f?text=Breakfast",
            },
            {
              caption: "",
              imageAlt: "Wood-fired sauna",
              imageUrl: "https://placehold.co/900x900/d8d2c4/4a4032?text=Sauna",
            },
            {
              caption: "",
              imageAlt: "The walled garden",
              imageUrl:
                "https://placehold.co/900x900/c9d6c4/3f5340?text=Garden",
            },
          ],
          columns: "3",
          eyebrow: "THE PROPERTY",
          heading: "Mornings, afternoons, evenings",
          rounded: "2xl",
          variant: "feature",
          ctaLabel: "See the full gallery",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          alignment: "left",
          textColor: "#0f172a",
          subheading:
            "A glimpse of the rooms, grounds, and the small everyday moments at Pine Hollow Lodge.",
          showCaptions: false,
          backgroundColor: "#ffffff",
          ctaOpenInNewTab: false,
        },
      },
      {
        type: "HostStory",
        props: {
          id: "HostStory-54f92efe-0731-4a8d-885a-6dffd760919c",
          body: "Pine Hollow Lodge is a twelve-room boutique stay in the hills above the coast. We bought the property in 2019, restored it room by room, and opened to guests in 2022. The garden is older than the house. Mornings are slow, breakfast is long, and we keep things small on purpose — so we can actually know who's staying with us.",
          ctaUrl: "/about",
          eyebrow: "Our story",
          heading:
            "We took over an old farmhouse and slowly turned it into a place we'd want to stay in ourselves.",
          variant: "image-left",
          ctaLabel: "Read more about the lodge",
          imageAlt: "Elena and Marco, hosts at Pine Hollow Lodge",
          imageUrl: "https://placehold.co/900x1100/efeae2/8b6f4e?text=Hosts",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          textColor: "#0f172a",
          accentColor: "#6b5b3e",
          signatureName: "Elena & Marco",
          signatureRole: "Hosts",
          backgroundColor: "#ffffff",
          ctaOpenInNewTab: false,
        },
      },
      {
        type: "OfferCards",
        props: {
          id: "OfferCards-0e3b71ef-f34f-4cac-b715-d39b0ae93292",
          items: [
            {
              badge: "Autumn",
              title: "Three nights in the olive grove",
              ctaUrl: "/contact?offer=autumn",
              ctaLabel: "Send inquiry",
              validity: "Available Sep–Nov, subject to availability",
              priceNote: "From €540 for 3 nights",
              inclusions: [
                { text: "3 nights in any garden room or suite" },
                { text: "Slow breakfast each morning" },
                { text: "Wood-fired sauna evenings" },
                { text: "One guided ridge walk with our host" },
              ],
              description:
                "Slow autumn stay with breakfast, sauna, and one guided walk in the hills.",
              ctaOpenInNewTab: false,
            },
            {
              badge: "Direct only",
              title: "Best rate promise",
              ctaUrl: "/contact?offer=direct",
              ctaLabel: "Check availability",
              validity: "All year",
              priceNote: "From €180 / night",
              inclusions: [
                { text: "Lowest available rate, guaranteed" },
                { text: "Welcome bottle of local wine" },
                { text: "Late checkout (12pm) when available" },
                { text: "Flexible date changes up to 7 days before" },
              ],
              description:
                "Book direct and we'll match any rate you find online — plus a welcome bottle and late checkout.",
              ctaOpenInNewTab: false,
            },
            {
              badge: "Winter",
              title: "Long-stay writer's retreat",
              ctaUrl: "/contact?offer=retreat",
              ctaLabel: "Plan a long stay",
              validity: "Available Jan–Mar",
              priceNote: "From €130 / night on 7+ nights",
              inclusions: [
                { text: "7+ nights at a reduced nightly rate" },
                { text: "Private workspace in your room" },
                { text: "Daily breakfast & one weekly dinner" },
                { text: "Laundry service included" },
              ],
              description:
                "Seven or more nights with a private workspace, slow mornings, and a quiet house.",
              ctaOpenInNewTab: false,
            },
          ],
          columns: "3",
          eyebrow: "DIRECT STAY PERKS",
          heading: "Better when you book with us",
          variant: "highlight",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          alignment: "left",
          textColor: "#0f172a",
          subheading:
            "Seasonal stays and direct-booking perks you won't find on the big travel sites.",
          accentColor: "#6b5b3e",
          cardTextColor: "#0f172a",
          backgroundColor: "#fafaf9",
          cardBackgroundColor: "#ffffff",
        },
      },
      {
        type: "DestinationGuide",
        props: {
          id: "DestinationGuide-3f8f00aa-9b3a-4c98-89ce-9f9123f85aa9",
          items: [
            {
              title: "Cala dei Pini",
              category: "Swim",
              distance: "8 min drive",
              imageAlt: "Cala dei Pini cove",
              imageUrl:
                "https://placehold.co/1200x900/cfd9d7/3f5853?text=Cala+dei+Pini",
              description:
                "A small cove framed by parasol pines. Quiet outside July and August.",
            },
            {
              title: "Trattoria del Borgo",
              category: "Eat",
              distance: "12 min walk",
              imageAlt: "Trattoria interior",
              imageUrl:
                "https://placehold.co/1200x900/efeae2/8b6f4e?text=Trattoria",
              description:
                "Family-run trattoria with handmade pasta and a garden out back. Book ahead.",
            },
            {
              title: "Sunset ridge trail",
              category: "Walk",
              distance: "From the door",
              imageAlt: "Ridge trail at sunset",
              imageUrl:
                "https://placehold.co/1200x900/c9d6c4/3f5340?text=Ridge+Trail",
              description:
                "A gentle one-hour loop with sweeping views over the valley and sea.",
            },
            {
              title: "Saturday market square",
              category: "Market",
              distance: "15 min drive",
              imageAlt: "Saturday market square",
              imageUrl:
                "https://placehold.co/1200x900/e7dccb/6a4b1f?text=Market",
              description:
                "Farmers, cheese, olive oil, and the best focaccia in the region. Mornings only.",
            },
          ],
          ctaUrl: "/local-guide",
          columns: "4",
          eyebrow: "THE AREA",
          heading: "Five minutes from our door",
          variant: "with-image",
          ctaLabel: "Read the full local guide",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          alignment: "left",
          textColor: "#0f172a",
          subheading:
            "Hand-picked places we'd send our own friends to — beaches, trails, restaurants, and the quiet corners.",
          accentColor: "#6b5b3e",
          backgroundColor: "#fafaf9",
          ctaOpenInNewTab: false,
        },
      },
      {
        type: "InquiryBand",
        props: {
          id: "InquiryBand-eeb11c2a-f6f9-4a51-8c20-9ab7994c41c1",
          eyebrow: "Plan your stay",
          heading: "Plan your stay with a real person",
          variant: "calm",
          imageAlt: "Pine Hollow Lodge at dusk",
          imageUrl: "https://placehold.co/1600x900/efeae2/8b6f4e?text=Lodge",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          textColor: "#0f172a",
          trustNote:
            "We reply within one business day · Real humans, no booking-engine bots",
          emailLabel: "Email",
          phoneLabel: "Call us",
          subheading:
            "Share your dates and a little about your trip. We respond within one business day, often the same morning.",
          accentColor: "#0f172a",
          phoneNumber: "+39 000 000 0000",
          ctaTextColor: "#ffffff",
          emailAddress: "stay@pinehollow.example",
          primaryCtaUrl: "/contact",
          showSecondary: true,
          whatsappLabel: "WhatsApp",
          whatsappNumber: "+39 000 000 0000",
          backgroundColor: "#f5f3ee",
          primaryCtaLabel: "Send an inquiry",
          primaryCtaOpenInNewTab: false,
        },
      },
      {
        type: "Footer",
        props: {
          id: "Footer-724c0927-4cb1-4719-a033-ab632d2d027b",
          columns: [
            {
              links: [
                {
                  href: "/rooms",
                  label: "Rooms & Suites",
                  openInNewTab: false,
                },
                {
                  href: "/cabins",
                  label: "Cabins & Villas",
                  openInNewTab: false,
                },
                {
                  href: "/offers",
                  label: "Offers & Packages",
                  openInNewTab: false,
                },
                {
                  href: "/gift-vouchers",
                  label: "Gift Vouchers",
                  openInNewTab: false,
                },
              ],
              heading: "Stay",
            },
            {
              links: [
                {
                  href: "/amenities",
                  label: "Amenities",
                  openInNewTab: false,
                },
                { href: "/gallery", label: "Gallery", openInNewTab: false },
                {
                  href: "/local-guide",
                  label: "Local Guide",
                  openInNewTab: false,
                },
                { href: "/about", label: "Our Story", openInNewTab: false },
              ],
              heading: "Explore",
            },
            {
              links: [
                {
                  href: "/contact",
                  label: "Send an Inquiry",
                  openInNewTab: false,
                },
                { href: "/faq", label: "FAQ", openInNewTab: false },
                {
                  href: "/policies",
                  label: "House Policies",
                  openInNewTab: false,
                },
                {
                  href: "/directions",
                  label: "Directions",
                  openInNewTab: false,
                },
              ],
              heading: "Plan your stay",
            },
          ],
          tagline:
            "A small, independently owned lodge. Hosted in person, written to by hand, and easy to reach for direct stays.",
          logoHref: "/",
          logoText: "Pine Hollow Lodge",
          logoType: "text",
          maxWidth: "xl",
          paddingX: "xl",
          paddingY: "sm",
          borderTop: false,
          textColor: "#000000",
          accentColor: "#171717",
          bottomBarBg: "#FFFFFF",
          bottomLinks: [
            { href: "/privacy", label: "Privacy", openInNewTab: false },
            { href: "/terms", label: "Terms", openInNewTab: false },
            { href: "/cookies", label: "Cookies", openInNewTab: false },
          ],
          linkOpacity: 70,
          socialLinks: [],
          dividerColor: "#FFFFFF",
          linkFontSize: 14,
          logoFontSize: 22,
          logoImageAlt: "Lodge logo",
          logoImageUrl: "",
          columnsLayout: "3",
          copyrightText: "© 2026 Pine Hollow Lodge. All rights reserved.",
          showBottomBar: true,
          socialIconGap: 12,
          borderTopColor: "",
          borderTopWidth: 1,
          linkHoverColor: "",
          logoFontWeight: "bold",
          logoImageWidth: 0,
          socialIconSize: 20,
          taglineOpacity: 80,
          backgroundColor: "#FFFFFF",
          logoColumnWidth: "50%",
          logoImageHeight: 38,
          taglineFontSize: 16,
          taglineMaxWidth: 380,
          bottomBarOpacity: 100,
          linkHoverOpacity: 100,
          bottomBarFontSize: 14,
          socialIconOpacity: 60,
          bottomBarTextColor: "#696969",
          bottomBarBorderColor: "#d4d4d4",
          columnHeadingFontSize: 14,
          logoImageBorderRadius: "none",
          columnHeadingTransform: "capitalize",
          columnHeadingLetterSpacing: "normal",
        },
      },
    ],
  },
};

// ─── 2. Villa / single-property rental — Homepage ───────────────────────────

const villaRentalHome: PuckTemplate = {
  id: "villa-rental-home",
  label: "Villa rental — Single-property homepage",
  description:
    "Story-led hero split, single accommodation card, gallery, local guide, inquiry. Built for one property.",
  suggestedSlug: "home",
  suggestedTitle: "Home",
  puckData: {
    root: {
      props: {
        title: "Casa dei Pini",
        fontFamily: "Inter",
        backgroundColor: "#ffffff",
      },
    },
    zones: {},
    content: [
      {
        type: "Navbar",
        props: {
          id: "Navbar-ff09a8f0-72d0-4ae2-add1-ffdb61a0ef5d",
          links: [
            {
              href: "/stay",
              label: "Stay",
              hasDropdown: true,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [
                {
                  links: [
                    {
                      href: "/rooms-garden-suite",
                      icon: "MatrialIconsHotel",
                      label: "Rooms & Suites",
                      description: "Browse our individually styled rooms.",
                      subSections: [],
                    },
                    {
                      href: "/cabins",
                      icon: "MatrialIconsApartment",
                      label: "Cabins & Villas",
                      description: "Private cabins for couples and families.",
                      subSections: [],
                    },
                    {
                      href: "/property",
                      icon: "FluentTent28Filled",
                      label: "The Property",
                      description: "Grounds, gardens, and shared spaces.",
                      subSections: [],
                    },
                  ],
                  title: "",
                },
              ],
            },
            {
              href: "/amenities",
              label: "Amenities",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/gallery",
              label: "Gallery",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/local-guide",
              label: "Local Guide",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/offers",
              label: "Offers",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/faq",
              label: "FAQ",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/contact",
              label: "Contact",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
          ],
          style: "blur",
          shadow: "none",
          ctaHref: "/contact",
          linkGap: 8,
          ctaLabel: "Send Inquiry",
          logoHref: "/",
          logoText: "Pine Hollow Lodge",
          logoType: "text",
          maxWidth: "2xl",
          paddingX: "md",
          paddingY: "none",
          textColor: "#111827",
          ctaVariant: "solid",
          ctaFontSize: 14,
          ctaPaddingX: 16,
          ctaPaddingY: 9,
          linkOpacity: 80,
          linksCtaGap: 8,
          backdropBlur: 10,
          borderBottom: true,
          ctaTextColor: "#FFFFFF",
          dividerColor: "",
          linkFontSize: 14,
          logoFontSize: 17,
          logoImageAlt: "Lodge logo",
          logoImageUrl: "",
          logoLinksGap: 33,
          navbarHeight: 64,
          positionType: "sticky",
          linkTextColor: "#111827",
          ctaBorderColor: "#111827",
          linkFontWeight: "medium",
          linksAlignment: "center",
          logoFontWeight: "semibold",
          logoImageWidth: 0,
          backgroundColor: "#FFFFFF",
          ctaBorderRadius: "full",
          ctaOpenInNewTab: false,
          logoImageHeight: 28,
          mobileMenuStyle: "fullscreen-right",
          logoImageBgColor: "transparent",
          logoImagePadding: 0,
          backgroundOpacity: 80,
          borderBottomColor: "#e5e7eb",
          borderBottomWidth: 1,
          ctaHoverTextColor: "#FFFFFF",
          ctaBackgroundColor: "#111827",
          ctaHoverBorderColor: "#1f2937",
          logoImageBorderRadius: "none",
          ctaHoverBackgroundColor: "#1f2937",
        },
      },
      {
        type: "HeroSplit",
        props: {
          id: "HeroSplit-34ba4f5e-cb7c-4843-bf3b-bb767b9d3d0c",
          preset: "minimal-editorial",
          actions: [],
          heading: "Casa dei Pini — your own villa above the bay",
          imageAlt: "Hero image",
          imageUrl:
            "https://placehold.co/600x500/e2e8f0/64748b?text=Your+Image",
          paddingX: "md",
          paddingY: "md",
          textColor: "#0f172a",
          subheading:
            "A four-bedroom stone house with a private garden, sea breeze, and a host who picks you up at the station.",
          imageShadow: true,
          headingColor: "",
          imageRounded: "xl",
          columnSpacing: 24,
          headingWeight: "extrabold",
          imagePosition: "right",
          textAlignment: "left",
          backgroundColor: "#ffffff",
          headingFontSize: 0,
          subheadingColor: "",
          subheadingOpacity: 80,
          subheadingFontSize: 0,
        },
      },
      {
        type: "AccommodationCard",
        props: {
          id: "AccommodationCard-45fc67ca-87dd-45e6-9d0e-dcd366473910",
          size: "32 m²",
          price: "€180",
          title: "Casa dei Pini",
          ctaUrl: "/rooms/garden-suite",
          shadow: "md",
          bedType: "1 King bed",
          eyebrow: "The villa",
          rounded: "2xl",
          variant: "image-first",
          capacity: "Sleeps 2",
          ctaLabel: "Send inquiry",
          imageAlt: "Garden Suite at Pine Hollow Lodge",
          imageUrl:
            "https://placehold.co/1200x800/efeae2/8b6f4e?text=Garden+Suite",
          amenities: [
            { text: "Garden view" },
            { text: "Breakfast included" },
            { text: "Free Wi-Fi" },
            { text: "Air conditioning" },
          ],
          textColor: "#0f172a",
          accentColor: "#0f172a",
          description:
            "A private suite opening onto the walled garden, with a king bed, deep soaking tub, and morning light through the olive trees.",
          priceSuffix: "/ night",
          priceFromLabel: "From",
          backgroundColor: "#ffffff",
          ctaOpenInNewTab: false,
        },
      },
      {
        type: "AmenitiesGrid",
        props: {
          id: "AmenitiesGrid-0ee7d1ff-3506-4ac0-9be2-f41bf2f0b8df",
          items: [
            {
              icon: "breakfast",
              title: "Sourdough breakfast",
              description:
                "Slow breakfast each morning with sourdough, garden fruit, and local cheeses.",
            },
            {
              icon: "pool",
              title: "Wood-fired sauna",
              description:
                "Heated each evening, with a cold plunge under the pines.",
            },
            {
              icon: "coffee",
              title: "Honesty bar",
              description:
                "Local wines, amari, and craft beers from the village down the hill.",
            },
            {
              icon: "wifi",
              title: "Fast Wi-Fi everywhere",
              description:
                "Full-property mesh — works just as well on the terrace.",
            },
            {
              icon: "parking",
              title: "Free on-site parking",
              description:
                "Shaded spaces beside your room — no need to move the car.",
            },
            {
              icon: "pet",
              title: "Pet-friendly cabins",
              description: "Two cabins welcome dogs at no extra charge.",
            },
            {
              icon: "local",
              title: "Local experiences desk",
              description:
                "We'll book the boat trip, the truffle hunt, or just point you toward the right bakery.",
            },
            {
              icon: "fire",
              title: "Evening fire pit",
              description: "Logs and blankets out by the olive grove from 8pm.",
            },
          ],
          layout: "card",
          columns: "4",
          eyebrow: "What's included",
          heading: "Comforts you'll actually use",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          alignment: "left",
          iconColor: "#6b5b3e",
          textColor: "#0f172a",
          subheading:
            "Everything is included in your stay — no resort fees, no surprises.",
          accentColor: "#6b5b3e",
          backgroundColor: "#ffffff",
        },
      },
      {
        type: "GallerySection",
        props: {
          id: "GallerySection-6d3aeb22-7630-4b94-ba87-c0373caa0598",
          gap: "md",
          ctaUrl: "/gallery",
          images: [
            {
              caption: "The Garden Suite at first light",
              imageAlt: "Garden Suite interior",
              imageUrl:
                "https://placehold.co/1600x1200/efeae2/8b6f4e?text=Garden+Suite",
            },
            {
              caption: "",
              imageAlt: "Pine Cabin exterior",
              imageUrl:
                "https://placehold.co/900x900/dde7d6/4b6043?text=Pine+Cabin",
            },
            {
              caption: "",
              imageAlt: "Breakfast on the terrace",
              imageUrl:
                "https://placehold.co/900x900/e7dccb/6a4b1f?text=Breakfast",
            },
            {
              caption: "",
              imageAlt: "Wood-fired sauna",
              imageUrl: "https://placehold.co/900x900/d8d2c4/4a4032?text=Sauna",
            },
            {
              caption: "",
              imageAlt: "The walled garden",
              imageUrl:
                "https://placehold.co/900x900/c9d6c4/3f5340?text=Garden",
            },
          ],
          columns: "3",
          eyebrow: "INSIDE CASA DEI PINI",
          heading: "Spaces designed for slow days",
          rounded: "2xl",
          variant: "feature",
          ctaLabel: "See the full gallery",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          alignment: "left",
          textColor: "#0f172a",
          subheading:
            "A glimpse of the rooms, grounds, and the small everyday moments at Pine Hollow Lodge.",
          showCaptions: false,
          backgroundColor: "#ffffff",
          ctaOpenInNewTab: false,
        },
      },
      {
        type: "DestinationGuide",
        props: {
          id: "DestinationGuide-83a69989-88d8-419b-aeab-a5cd8dd7af78",
          items: [
            {
              title: "Cala dei Pini",
              category: "Swim",
              distance: "8 min drive",
              imageAlt: "Cala dei Pini cove",
              imageUrl:
                "https://placehold.co/1200x900/cfd9d7/3f5853?text=Cala+dei+Pini",
              description:
                "A small cove framed by parasol pines. Quiet outside July and August.",
            },
            {
              title: "Trattoria del Borgo",
              category: "Eat",
              distance: "12 min walk",
              imageAlt: "Trattoria interior",
              imageUrl:
                "https://placehold.co/1200x900/efeae2/8b6f4e?text=Trattoria",
              description:
                "Family-run trattoria with handmade pasta and a garden out back. Book ahead.",
            },
            {
              title: "Sunset ridge trail",
              category: "Walk",
              distance: "From the door",
              imageAlt: "Ridge trail at sunset",
              imageUrl:
                "https://placehold.co/1200x900/c9d6c4/3f5340?text=Ridge+Trail",
              description:
                "A gentle one-hour loop with sweeping views over the valley and sea.",
            },
            {
              title: "Saturday market square",
              category: "Market",
              distance: "15 min drive",
              imageAlt: "Saturday market square",
              imageUrl:
                "https://placehold.co/1200x900/e7dccb/6a4b1f?text=Market",
              description:
                "Farmers, cheese, olive oil, and the best focaccia in the region. Mornings only.",
            },
          ],
          ctaUrl: "/local-guide",
          columns: "4",
          eyebrow: "THE COAST",
          heading: "Where the locals go",
          variant: "cards",
          ctaLabel: "Read the full local guide",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          alignment: "left",
          textColor: "#0f172a",
          subheading:
            "Hand-picked places we'd send our own friends to — beaches, trails, restaurants, and the quiet corners.",
          accentColor: "#6b5b3e",
          backgroundColor: "#fafaf9",
          ctaOpenInNewTab: false,
        },
      },
      {
        type: "InquiryBand",
        props: {
          id: "InquiryBand-eeb11c2a-f6f9-4a51-8c20-9ab7994c41c1",
          eyebrow: "Plan your stay",
          heading: "Tell us about your stay",
          variant: "calm",
          imageAlt: "Pine Hollow Lodge at dusk",
          imageUrl: "https://placehold.co/1600x900/efeae2/8b6f4e?text=Lodge",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          textColor: "#0f172a",
          trustNote:
            "We reply within one business day · Real humans, no booking-engine bots",
          emailLabel: "Email",
          phoneLabel: "Call us",
          subheading:
            "Share your dates and a little about your trip. We respond within one business day, often the same morning.",
          accentColor: "#0f172a",
          phoneNumber: "+39 000 000 0000",
          ctaTextColor: "#ffffff",
          emailAddress: "stay@pinehollow.example",
          primaryCtaUrl: "/contact",
          showSecondary: true,
          whatsappLabel: "WhatsApp",
          whatsappNumber: "+39 000 000 0000",
          backgroundColor: "#f5f3ee",
          primaryCtaLabel: "Send an inquiry",
          primaryCtaOpenInNewTab: false,
        },
      },
      {
        type: "Footer",
        props: {
          id: "Footer-724c0927-4cb1-4719-a033-ab632d2d027b",
          columns: [
            {
              links: [
                {
                  href: "/rooms",
                  label: "Rooms & Suites",
                  openInNewTab: false,
                },
                {
                  href: "/cabins",
                  label: "Cabins & Villas",
                  openInNewTab: false,
                },
                {
                  href: "/offers",
                  label: "Offers & Packages",
                  openInNewTab: false,
                },
                {
                  href: "/gift-vouchers",
                  label: "Gift Vouchers",
                  openInNewTab: false,
                },
              ],
              heading: "Stay",
            },
            {
              links: [
                {
                  href: "/amenities",
                  label: "Amenities",
                  openInNewTab: false,
                },
                { href: "/gallery", label: "Gallery", openInNewTab: false },
                {
                  href: "/local-guide",
                  label: "Local Guide",
                  openInNewTab: false,
                },
                { href: "/about", label: "Our Story", openInNewTab: false },
              ],
              heading: "Explore",
            },
            {
              links: [
                {
                  href: "/contact",
                  label: "Send an Inquiry",
                  openInNewTab: false,
                },
                { href: "/faq", label: "FAQ", openInNewTab: false },
                {
                  href: "/policies",
                  label: "House Policies",
                  openInNewTab: false,
                },
                {
                  href: "/directions",
                  label: "Directions",
                  openInNewTab: false,
                },
              ],
              heading: "Plan your stay",
            },
          ],
          tagline:
            "A small, independently owned lodge. Hosted in person, written to by hand, and easy to reach for direct stays.",
          logoHref: "/",
          logoText: "Pine Hollow Lodge",
          logoType: "text",
          maxWidth: "xl",
          paddingX: "xl",
          paddingY: "sm",
          borderTop: false,
          textColor: "#000000",
          accentColor: "#171717",
          bottomBarBg: "#FFFFFF",
          bottomLinks: [
            { href: "/privacy", label: "Privacy", openInNewTab: false },
            { href: "/terms", label: "Terms", openInNewTab: false },
            { href: "/cookies", label: "Cookies", openInNewTab: false },
          ],
          linkOpacity: 70,
          socialLinks: [],
          dividerColor: "#FFFFFF",
          linkFontSize: 14,
          logoFontSize: 22,
          logoImageAlt: "Lodge logo",
          logoImageUrl: "",
          columnsLayout: "3",
          copyrightText: "© 2026 Pine Hollow Lodge. All rights reserved.",
          showBottomBar: true,
          socialIconGap: 12,
          borderTopColor: "",
          borderTopWidth: 1,
          linkHoverColor: "",
          logoFontWeight: "bold",
          logoImageWidth: 0,
          socialIconSize: 20,
          taglineOpacity: 80,
          backgroundColor: "#FFFFFF",
          logoColumnWidth: "50%",
          logoImageHeight: 38,
          taglineFontSize: 16,
          taglineMaxWidth: 380,
          bottomBarOpacity: 100,
          linkHoverOpacity: 100,
          bottomBarFontSize: 14,
          socialIconOpacity: 60,
          bottomBarTextColor: "#696969",
          bottomBarBorderColor: "#d4d4d4",
          columnHeadingFontSize: 14,
          logoImageBorderRadius: "none",
          columnHeadingTransform: "capitalize",
          columnHeadingLetterSpacing: "normal",
        },
      },
    ],
  },
};

// ─── 3. Accommodation detail page ───────────────────────────────────────────

const accommodationDetail: PuckTemplate = {
  id: "accommodation-detail",
  label: "Accommodation — Room or suite detail",
  description:
    "Hero split with the room photo, amenities, room gallery, room-specific offers, inquiry.",
  suggestedSlug: "/rooms/garden-suite",
  suggestedTitle: "Garden Suite",
  puckData: {
    root: {
      props: {
        title: "Garden Suite",
        fontFamily: "Inter",
        backgroundColor: "#ffffff",
      },
    },
    zones: {},
    content: [
      {
        type: "Navbar",
        props: {
          id: "Navbar-ff09a8f0-72d0-4ae2-add1-ffdb61a0ef5d",
          links: [
            {
              href: "/stay",
              label: "Stay",
              hasDropdown: true,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [
                {
                  links: [
                    {
                      href: "/rooms-garden-suite",
                      icon: "MatrialIconsHotel",
                      label: "Rooms & Suites",
                      description: "Browse our individually styled rooms.",
                      subSections: [],
                    },
                    {
                      href: "/cabins",
                      icon: "MatrialIconsApartment",
                      label: "Cabins & Villas",
                      description: "Private cabins for couples and families.",
                      subSections: [],
                    },
                    {
                      href: "/property",
                      icon: "FluentTent28Filled",
                      label: "The Property",
                      description: "Grounds, gardens, and shared spaces.",
                      subSections: [],
                    },
                  ],
                  title: "",
                },
              ],
            },
            {
              href: "/amenities",
              label: "Amenities",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/gallery",
              label: "Gallery",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/local-guide",
              label: "Local Guide",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/offers",
              label: "Offers",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/faq",
              label: "FAQ",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/contact",
              label: "Contact",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
          ],
          style: "blur",
          shadow: "none",
          ctaHref: "/contact",
          linkGap: 8,
          ctaLabel: "Send Inquiry",
          logoHref: "/",
          logoText: "Pine Hollow Lodge",
          logoType: "text",
          maxWidth: "2xl",
          paddingX: "md",
          paddingY: "none",
          textColor: "#111827",
          ctaVariant: "solid",
          ctaFontSize: 14,
          ctaPaddingX: 16,
          ctaPaddingY: 9,
          linkOpacity: 80,
          linksCtaGap: 8,
          backdropBlur: 10,
          borderBottom: true,
          ctaTextColor: "#FFFFFF",
          dividerColor: "",
          linkFontSize: 14,
          logoFontSize: 17,
          logoImageAlt: "Lodge logo",
          logoImageUrl: "",
          logoLinksGap: 33,
          navbarHeight: 64,
          positionType: "sticky",
          linkTextColor: "#111827",
          ctaBorderColor: "#111827",
          linkFontWeight: "medium",
          linksAlignment: "center",
          logoFontWeight: "semibold",
          logoImageWidth: 0,
          backgroundColor: "#FFFFFF",
          ctaBorderRadius: "full",
          ctaOpenInNewTab: false,
          logoImageHeight: 28,
          mobileMenuStyle: "fullscreen-right",
          logoImageBgColor: "transparent",
          logoImagePadding: 0,
          backgroundOpacity: 80,
          borderBottomColor: "#e5e7eb",
          borderBottomWidth: 1,
          ctaHoverTextColor: "#FFFFFF",
          ctaBackgroundColor: "#111827",
          ctaHoverBorderColor: "#1f2937",
          logoImageBorderRadius: "none",
          ctaHoverBackgroundColor: "#1f2937",
        },
      },
      {
        type: "Breadcrumb",
        props: {
          id: "Breadcrumb-c7464e38-4d4e-4b55-9cea-e11a0689acde",
          size: "md",
          color: "",
          items: [
            { href: "/", label: "Home" },
            { href: "/products", label: "Products" },
            { href: "", label: "Current Page" },
          ],
          separator: "chevron",
        },
      },
      {
        type: "HeroSplit",
        props: {
          id: "HeroSplit-a5271327-2f92-4a3b-bab1-ffbfcb5ca94d",
          preset: "lifestyle-photo",
          actions: [],
          heading: "Garden Suite — private terrace, slow mornings",
          imageAlt: "Hero image",
          imageUrl:
            "https://placehold.co/600x500/e2e8f0/64748b?text=Your+Image",
          paddingX: "md",
          paddingY: "md",
          textColor: "#0f172a",
          subheading:
            "Our largest room, looking onto the walled garden. King bed, deep tub, breakfast outside when the weather is kind.",
          imageShadow: true,
          headingColor: "",
          imageRounded: "xl",
          columnSpacing: 24,
          headingWeight: "extrabold",
          imagePosition: "right",
          textAlignment: "left",
          backgroundColor: "#ffffff",
          headingFontSize: 0,
          subheadingColor: "",
          subheadingOpacity: 80,
          subheadingFontSize: 0,
        },
      },
      {
        type: "AmenitiesGrid",
        props: {
          id: "AmenitiesGrid-0ee7d1ff-3506-4ac0-9be2-f41bf2f0b8df",
          items: [
            {
              icon: "breakfast",
              title: "Sourdough breakfast",
              description:
                "Slow breakfast each morning with sourdough, garden fruit, and local cheeses.",
            },
            {
              icon: "pool",
              title: "Wood-fired sauna",
              description:
                "Heated each evening, with a cold plunge under the pines.",
            },
            {
              icon: "coffee",
              title: "Honesty bar",
              description:
                "Local wines, amari, and craft beers from the village down the hill.",
            },
            {
              icon: "wifi",
              title: "Fast Wi-Fi everywhere",
              description:
                "Full-property mesh — works just as well on the terrace.",
            },
            {
              icon: "parking",
              title: "Free on-site parking",
              description:
                "Shaded spaces beside your room — no need to move the car.",
            },
            {
              icon: "pet",
              title: "Pet-friendly cabins",
              description: "Two cabins welcome dogs at no extra charge.",
            },
            {
              icon: "local",
              title: "Local experiences desk",
              description:
                "We'll book the boat trip, the truffle hunt, or just point you toward the right bakery.",
            },
            {
              icon: "fire",
              title: "Evening fire pit",
              description: "Logs and blankets out by the olive grove from 8pm.",
            },
          ],
          layout: "card",
          columns: "4",
          eyebrow: "IN THIS ROOM",
          heading: "What's included",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          alignment: "left",
          iconColor: "#6b5b3e",
          textColor: "#0f172a",
          subheading:
            "Everything is included in your stay — no resort fees, no surprises.",
          accentColor: "#6b5b3e",
          backgroundColor: "#ffffff",
        },
      },
      {
        type: "GallerySection",
        props: {
          id: "GallerySection-6d3aeb22-7630-4b94-ba87-c0373caa0598",
          gap: "md",
          ctaUrl: "/gallery",
          images: [
            {
              caption: "The Garden Suite at first light",
              imageAlt: "Garden Suite interior",
              imageUrl:
                "https://placehold.co/1600x1200/efeae2/8b6f4e?text=Garden+Suite",
            },
            {
              caption: "",
              imageAlt: "Pine Cabin exterior",
              imageUrl:
                "https://placehold.co/900x900/dde7d6/4b6043?text=Pine+Cabin",
            },
            {
              caption: "",
              imageAlt: "Breakfast on the terrace",
              imageUrl:
                "https://placehold.co/900x900/e7dccb/6a4b1f?text=Breakfast",
            },
            {
              caption: "",
              imageAlt: "Wood-fired sauna",
              imageUrl: "https://placehold.co/900x900/d8d2c4/4a4032?text=Sauna",
            },
            {
              caption: "",
              imageAlt: "The walled garden",
              imageUrl:
                "https://placehold.co/900x900/c9d6c4/3f5340?text=Garden",
            },
          ],
          columns: "3",
          eyebrow: "INSIDE THE GARDEN SUITE",
          heading: "A closer look",
          rounded: "2xl",
          variant: "grid",
          ctaLabel: "See the full gallery",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          alignment: "left",
          textColor: "#0f172a",
          subheading:
            "A glimpse of the rooms, grounds, and the small everyday moments at Pine Hollow Lodge.",
          showCaptions: false,
          backgroundColor: "#ffffff",
          ctaOpenInNewTab: false,
        },
      },
      {
        type: "OfferCards",
        props: {
          id: "OfferCards-0e3b71ef-f34f-4cac-b715-d39b0ae93292",
          items: [
            {
              badge: "Autumn",
              title: "Three nights in the olive grove",
              ctaUrl: "/contact?offer=autumn",
              ctaLabel: "Send inquiry",
              validity: "Available Sep–Nov, subject to availability",
              priceNote: "From €540 for 3 nights",
              inclusions: [
                { text: "3 nights in any garden room or suite" },
                { text: "Slow breakfast each morning" },
                { text: "Wood-fired sauna evenings" },
                { text: "One guided ridge walk with our host" },
              ],
              description:
                "Slow autumn stay with breakfast, sauna, and one guided walk in the hills.",
              ctaOpenInNewTab: false,
            },
            {
              badge: "Direct only",
              title: "Best rate promise",
              ctaUrl: "/contact?offer=direct",
              ctaLabel: "Check availability",
              validity: "All year",
              priceNote: "From €180 / night",
              inclusions: [
                { text: "Lowest available rate, guaranteed" },
                { text: "Welcome bottle of local wine" },
                { text: "Late checkout (12pm) when available" },
                { text: "Flexible date changes up to 7 days before" },
              ],
              description:
                "Book direct and we'll match any rate you find online — plus a welcome bottle and late checkout.",
              ctaOpenInNewTab: false,
            },
            {
              badge: "Winter",
              title: "Long-stay writer's retreat",
              ctaUrl: "/contact?offer=retreat",
              ctaLabel: "Plan a long stay",
              validity: "Available Jan–Mar",
              priceNote: "From €130 / night on 7+ nights",
              inclusions: [
                { text: "7+ nights at a reduced nightly rate" },
                { text: "Private workspace in your room" },
                { text: "Daily breakfast & one weekly dinner" },
                { text: "Laundry service included" },
              ],
              description:
                "Seven or more nights with a private workspace, slow mornings, and a quiet house.",
              ctaOpenInNewTab: false,
            },
          ],
          columns: "3",
          eyebrow: "STAY LONGER",
          heading: "Direct booking perks for this room",
          variant: "highlight",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          alignment: "left",
          textColor: "#0f172a",
          subheading:
            "Seasonal stays and direct-booking perks you won't find on the big travel sites.",
          accentColor: "#6b5b3e",
          cardTextColor: "#0f172a",
          backgroundColor: "#fafaf9",
          cardBackgroundColor: "#ffffff",
        },
      },
      {
        type: "InquiryBand",
        props: {
          id: "InquiryBand-eeb11c2a-f6f9-4a51-8c20-9ab7994c41c1",
          eyebrow: "",
          heading: "Reserve the Garden Suite",
          variant: "compact",
          imageAlt: "Pine Hollow Lodge at dusk",
          imageUrl: "https://placehold.co/1600x900/efeae2/8b6f4e?text=Lodge",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          textColor: "#0f172a",
          trustNote:
            "We reply within one business day · Real humans, no booking-engine bots",
          emailLabel: "Email",
          phoneLabel: "Call us",
          subheading: "",
          accentColor: "#0f172a",
          phoneNumber: "+39 000 000 0000",
          ctaTextColor: "#ffffff",
          emailAddress: "stay@pinehollow.example",
          primaryCtaUrl: "/contact",
          showSecondary: true,
          whatsappLabel: "WhatsApp",
          whatsappNumber: "+39 000 000 0000",
          backgroundColor: "#f5f3ee",
          primaryCtaLabel: "Send an inquiry",
          primaryCtaOpenInNewTab: false,
        },
      },
      {
        type: "Footer",
        props: {
          id: "Footer-724c0927-4cb1-4719-a033-ab632d2d027b",
          columns: [
            {
              links: [
                {
                  href: "/rooms",
                  label: "Rooms & Suites",
                  openInNewTab: false,
                },
                {
                  href: "/cabins",
                  label: "Cabins & Villas",
                  openInNewTab: false,
                },
                {
                  href: "/offers",
                  label: "Offers & Packages",
                  openInNewTab: false,
                },
                {
                  href: "/gift-vouchers",
                  label: "Gift Vouchers",
                  openInNewTab: false,
                },
              ],
              heading: "Stay",
            },
            {
              links: [
                {
                  href: "/amenities",
                  label: "Amenities",
                  openInNewTab: false,
                },
                { href: "/gallery", label: "Gallery", openInNewTab: false },
                {
                  href: "/local-guide",
                  label: "Local Guide",
                  openInNewTab: false,
                },
                { href: "/about", label: "Our Story", openInNewTab: false },
              ],
              heading: "Explore",
            },
            {
              links: [
                {
                  href: "/contact",
                  label: "Send an Inquiry",
                  openInNewTab: false,
                },
                { href: "/faq", label: "FAQ", openInNewTab: false },
                {
                  href: "/policies",
                  label: "House Policies",
                  openInNewTab: false,
                },
                {
                  href: "/directions",
                  label: "Directions",
                  openInNewTab: false,
                },
              ],
              heading: "Plan your stay",
            },
          ],
          tagline:
            "A small, independently owned lodge. Hosted in person, written to by hand, and easy to reach for direct stays.",
          logoHref: "/",
          logoText: "Pine Hollow Lodge",
          logoType: "text",
          maxWidth: "xl",
          paddingX: "xl",
          paddingY: "sm",
          borderTop: false,
          textColor: "#000000",
          accentColor: "#171717",
          bottomBarBg: "#FFFFFF",
          bottomLinks: [
            { href: "/privacy", label: "Privacy", openInNewTab: false },
            { href: "/terms", label: "Terms", openInNewTab: false },
            { href: "/cookies", label: "Cookies", openInNewTab: false },
          ],
          linkOpacity: 70,
          socialLinks: [],
          dividerColor: "#FFFFFF",
          linkFontSize: 14,
          logoFontSize: 22,
          logoImageAlt: "Lodge logo",
          logoImageUrl: "",
          columnsLayout: "3",
          copyrightText: "© 2026 Pine Hollow Lodge. All rights reserved.",
          showBottomBar: true,
          socialIconGap: 12,
          borderTopColor: "",
          borderTopWidth: 1,
          linkHoverColor: "",
          logoFontWeight: "bold",
          logoImageWidth: 0,
          socialIconSize: 20,
          taglineOpacity: 80,
          backgroundColor: "#FFFFFF",
          logoColumnWidth: "50%",
          logoImageHeight: 38,
          taglineFontSize: 16,
          taglineMaxWidth: 380,
          bottomBarOpacity: 100,
          linkHoverOpacity: 100,
          bottomBarFontSize: 14,
          socialIconOpacity: 60,
          bottomBarTextColor: "#696969",
          bottomBarBorderColor: "#d4d4d4",
          columnHeadingFontSize: 14,
          logoImageBorderRadius: "none",
          columnHeadingTransform: "capitalize",
          columnHeadingLetterSpacing: "normal",
        },
      },
    ],
  },
};

// ─── 4. Contact / Inquiry page ──────────────────────────────────────────────

const contactInquiry: PuckTemplate = {
  id: "contact-inquiry",
  label: "Contact & inquiry page",
  description:
    "Inquiry band on top, full contact form below. The page guests land on from your nav.",
  suggestedSlug: "/contact",
  suggestedTitle: "Contact",
  puckData: {
    root: {
      props: {
        title: "Contact",
        fontFamily: "Inter",
        backgroundColor: "#ffffff",
      },
    },
    zones: {},
    content: [
      {
        type: "Navbar",
        props: {
          id: "Navbar-ff09a8f0-72d0-4ae2-add1-ffdb61a0ef5d",
          links: [
            {
              href: "/stay",
              label: "Stay",
              hasDropdown: true,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [
                {
                  links: [
                    {
                      href: "/rooms-garden-suite",
                      icon: "MatrialIconsHotel",
                      label: "Rooms & Suites",
                      description: "Browse our individually styled rooms.",
                      subSections: [],
                    },
                    {
                      href: "/cabins",
                      icon: "MatrialIconsApartment",
                      label: "Cabins & Villas",
                      description: "Private cabins for couples and families.",
                      subSections: [],
                    },
                    {
                      href: "/property",
                      icon: "FluentTent28Filled",
                      label: "The Property",
                      description: "Grounds, gardens, and shared spaces.",
                      subSections: [],
                    },
                  ],
                  title: "",
                },
              ],
            },
            {
              href: "/amenities",
              label: "Amenities",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/gallery",
              label: "Gallery",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/local-guide",
              label: "Local Guide",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/offers",
              label: "Offers",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/faq",
              label: "FAQ",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/contact",
              label: "Contact",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
          ],
          style: "blur",
          shadow: "none",
          ctaHref: "/contact",
          linkGap: 8,
          ctaLabel: "Send Inquiry",
          logoHref: "/",
          logoText: "Pine Hollow Lodge",
          logoType: "text",
          maxWidth: "2xl",
          paddingX: "md",
          paddingY: "none",
          textColor: "#111827",
          ctaVariant: "solid",
          ctaFontSize: 14,
          ctaPaddingX: 16,
          ctaPaddingY: 9,
          linkOpacity: 80,
          linksCtaGap: 8,
          backdropBlur: 10,
          borderBottom: true,
          ctaTextColor: "#FFFFFF",
          dividerColor: "",
          linkFontSize: 14,
          logoFontSize: 17,
          logoImageAlt: "Lodge logo",
          logoImageUrl: "",
          logoLinksGap: 33,
          navbarHeight: 64,
          positionType: "sticky",
          linkTextColor: "#111827",
          ctaBorderColor: "#111827",
          linkFontWeight: "medium",
          linksAlignment: "center",
          logoFontWeight: "semibold",
          logoImageWidth: 0,
          backgroundColor: "#FFFFFF",
          ctaBorderRadius: "full",
          ctaOpenInNewTab: false,
          logoImageHeight: 28,
          mobileMenuStyle: "fullscreen-right",
          logoImageBgColor: "transparent",
          logoImagePadding: 0,
          backgroundOpacity: 80,
          borderBottomColor: "#e5e7eb",
          borderBottomWidth: 1,
          ctaHoverTextColor: "#FFFFFF",
          ctaBackgroundColor: "#111827",
          ctaHoverBorderColor: "#1f2937",
          logoImageBorderRadius: "none",
          ctaHoverBackgroundColor: "#1f2937",
        },
      },
      {
        type: "InquiryBand",
        props: {
          id: "InquiryBand-eeb11c2a-f6f9-4a51-8c20-9ab7994c41c1",
          eyebrow: "Plan your stay",
          heading: "Plan your stay with us directly",
          variant: "split",
          imageAlt: "Pine Hollow Lodge at dusk",
          imageUrl: "https://placehold.co/1600x900/efeae2/8b6f4e?text=Lodge",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          textColor: "#0f172a",
          trustNote:
            "We reply within one business day · Real humans, no booking-engine bots",
          emailLabel: "Email",
          phoneLabel: "Call us",
          subheading:
            "Share your dates and a little about your trip. We respond within one business day, often the same morning.",
          accentColor: "#0f172a",
          phoneNumber: "+39 000 000 0000",
          ctaTextColor: "#ffffff",
          emailAddress: "stay@pinehollow.example",
          primaryCtaUrl: "/contact",
          showSecondary: true,
          whatsappLabel: "WhatsApp",
          whatsappNumber: "+39 000 000 0000",
          backgroundColor: "#f5f3ee",
          primaryCtaLabel: "Send an inquiry",
          primaryCtaOpenInNewTab: false,
        },
      },
      {
        type: "ContactSection",
        props: {
          id: "ContactSection-223a55d6-8cc8-4bce-a3aa-6ab9fd958d06",
          layout: "text-left",
          formKey: "contact-primary",
          heading: "Plan your stay",
          badgeText: "Direct booking assistance",
          emailNote: "Most inquiries receive a reply within one business day.",
          emailLabel: "reservations@example.com",
          bulletItems: [
            { text: "Direct availability guidance" },
            { text: "Room and villa recommendations" },
            { text: "Local arrival details" },
            { text: "Special occasion support" },
          ],
          description:
            "Share your preferred dates, group size, and any special requests. The reservations team will reply with tailored availability.",
          showContent: true,
          submitLabel: "Send Inquiry",
          successText:
            "Thanks. We received your inquiry and will reply within one business day.",
          emailAddress: "reservations@example.com",
          headingColor: "#1d4ed8",
          backgroundColor: "#ffffff",
          descriptionColor: "#334155",
          showEmailFallback: true,
        },
      },
      {
        type: "Footer",
        props: {
          id: "Footer-724c0927-4cb1-4719-a033-ab632d2d027b",
          columns: [
            {
              links: [
                {
                  href: "/rooms",
                  label: "Rooms & Suites",
                  openInNewTab: false,
                },
                {
                  href: "/cabins",
                  label: "Cabins & Villas",
                  openInNewTab: false,
                },
                {
                  href: "/offers",
                  label: "Offers & Packages",
                  openInNewTab: false,
                },
                {
                  href: "/gift-vouchers",
                  label: "Gift Vouchers",
                  openInNewTab: false,
                },
              ],
              heading: "Stay",
            },
            {
              links: [
                {
                  href: "/amenities",
                  label: "Amenities",
                  openInNewTab: false,
                },
                { href: "/gallery", label: "Gallery", openInNewTab: false },
                {
                  href: "/local-guide",
                  label: "Local Guide",
                  openInNewTab: false,
                },
                { href: "/about", label: "Our Story", openInNewTab: false },
              ],
              heading: "Explore",
            },
            {
              links: [
                {
                  href: "/contact",
                  label: "Send an Inquiry",
                  openInNewTab: false,
                },
                { href: "/faq", label: "FAQ", openInNewTab: false },
                {
                  href: "/policies",
                  label: "House Policies",
                  openInNewTab: false,
                },
                {
                  href: "/directions",
                  label: "Directions",
                  openInNewTab: false,
                },
              ],
              heading: "Plan your stay",
            },
          ],
          tagline:
            "A small, independently owned lodge. Hosted in person, written to by hand, and easy to reach for direct stays.",
          logoHref: "/",
          logoText: "Pine Hollow Lodge",
          logoType: "text",
          maxWidth: "xl",
          paddingX: "xl",
          paddingY: "sm",
          borderTop: false,
          textColor: "#000000",
          accentColor: "#171717",
          bottomBarBg: "#FFFFFF",
          bottomLinks: [
            { href: "/privacy", label: "Privacy", openInNewTab: false },
            { href: "/terms", label: "Terms", openInNewTab: false },
            { href: "/cookies", label: "Cookies", openInNewTab: false },
          ],
          linkOpacity: 70,
          socialLinks: [],
          dividerColor: "#FFFFFF",
          linkFontSize: 14,
          logoFontSize: 22,
          logoImageAlt: "Lodge logo",
          logoImageUrl: "",
          columnsLayout: "3",
          copyrightText: "© 2026 Pine Hollow Lodge. All rights reserved.",
          showBottomBar: true,
          socialIconGap: 12,
          borderTopColor: "",
          borderTopWidth: 1,
          linkHoverColor: "",
          logoFontWeight: "bold",
          logoImageWidth: 0,
          socialIconSize: 20,
          taglineOpacity: 80,
          backgroundColor: "#FFFFFF",
          logoColumnWidth: "50%",
          logoImageHeight: 38,
          taglineFontSize: 16,
          taglineMaxWidth: 380,
          bottomBarOpacity: 100,
          linkHoverOpacity: 100,
          bottomBarFontSize: 14,
          socialIconOpacity: 60,
          bottomBarTextColor: "#696969",
          bottomBarBorderColor: "#d4d4d4",
          columnHeadingFontSize: 14,
          logoImageBorderRadius: "none",
          columnHeadingTransform: "capitalize",
          columnHeadingLetterSpacing: "normal",
        },
      },
    ],
  },
};

// ─── 5. Local guide page ────────────────────────────────────────────────────

const localGuide: PuckTemplate = {
  id: "local-guide",
  label: "Local guide page",
  description:
    "Editorial hero, destination guide cards, inquiry band. Helps guests fall in love with the area.",
  suggestedSlug: "/guide",
  suggestedTitle: "The area",
  puckData: {
    root: {
      props: {
        title: "Local guide",
        fontFamily: "Inter",
        backgroundColor: "#ffffff",
      },
    },
    zones: {},
    content: [
      {
        type: "Navbar",
        props: {
          id: "Navbar-ff09a8f0-72d0-4ae2-add1-ffdb61a0ef5d",
          links: [
            {
              href: "/stay",
              label: "Stay",
              hasDropdown: true,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [
                {
                  links: [
                    {
                      href: "/rooms-garden-suite",
                      icon: "MatrialIconsHotel",
                      label: "Rooms & Suites",
                      description: "Browse our individually styled rooms.",
                      subSections: [],
                    },
                    {
                      href: "/cabins",
                      icon: "MatrialIconsApartment",
                      label: "Cabins & Villas",
                      description: "Private cabins for couples and families.",
                      subSections: [],
                    },
                    {
                      href: "/property",
                      icon: "FluentTent28Filled",
                      label: "The Property",
                      description: "Grounds, gardens, and shared spaces.",
                      subSections: [],
                    },
                  ],
                  title: "",
                },
              ],
            },
            {
              href: "/amenities",
              label: "Amenities",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/gallery",
              label: "Gallery",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/local-guide",
              label: "Local Guide",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/offers",
              label: "Offers",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/faq",
              label: "FAQ",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
            {
              href: "/contact",
              label: "Contact",
              hasDropdown: false,
              dropdownType: "simple",
              openInNewTab: false,
              dropdownSections: [],
            },
          ],
          style: "blur",
          shadow: "none",
          ctaHref: "/contact",
          linkGap: 8,
          ctaLabel: "Send Inquiry",
          logoHref: "/",
          logoText: "Pine Hollow Lodge",
          logoType: "text",
          maxWidth: "2xl",
          paddingX: "md",
          paddingY: "none",
          textColor: "#111827",
          ctaVariant: "solid",
          ctaFontSize: 14,
          ctaPaddingX: 16,
          ctaPaddingY: 9,
          linkOpacity: 80,
          linksCtaGap: 8,
          backdropBlur: 10,
          borderBottom: true,
          ctaTextColor: "#FFFFFF",
          dividerColor: "",
          linkFontSize: 14,
          logoFontSize: 17,
          logoImageAlt: "Lodge logo",
          logoImageUrl: "",
          logoLinksGap: 33,
          navbarHeight: 64,
          positionType: "sticky",
          linkTextColor: "#111827",
          ctaBorderColor: "#111827",
          linkFontWeight: "medium",
          linksAlignment: "center",
          logoFontWeight: "semibold",
          logoImageWidth: 0,
          backgroundColor: "#FFFFFF",
          ctaBorderRadius: "full",
          ctaOpenInNewTab: false,
          logoImageHeight: 28,
          mobileMenuStyle: "fullscreen-right",
          logoImageBgColor: "transparent",
          logoImagePadding: 0,
          backgroundOpacity: 80,
          borderBottomColor: "#e5e7eb",
          borderBottomWidth: 1,
          ctaHoverTextColor: "#FFFFFF",
          ctaBackgroundColor: "#111827",
          ctaHoverBorderColor: "#1f2937",
          logoImageBorderRadius: "none",
          ctaHoverBackgroundColor: "#1f2937",
        },
      },
      {
        type: "Hero",
        props: {
          id: "Hero-0fcc754f-d8be-4d0a-b825-433a0f728456",
          preset: "minimal-editorial",
          actions: [],
          heading: "Your quiet escape, framed by pine and lake",
          alignment: "center",
          minHeight: "lg",
          textColor: "#ffffff",
          subheading:
            "A hand-picked guide from your hosts — the trails, tables, and quiet hours we'd send our own friends to.",
          showActions: "show",
          headingColor: "",
          headingPrefix: "",
          headingSuffix: "",
          headingWeight: "extrabold",
          overlayOpacity: 50,
          backgroundColor: "#0f172a",
          backgroundImage: "",
          headingFontSize: 0,
          headingMaxWidth: 800,
          highlightColors: "#3B82F6,#8B5CF6,#EC4899,#F59E0B",
          subheadingColor: "",
          headingHighlight: "",
          subheadingWeight: "normal",
          subheadingOpacity: 90,
          highlightAnimation: "none",
          subheadingFontSize: 0,
          subheadingMaxWidth: 850,
          showHighlightUnderline: false,
          highlightUnderlineColor: "rgba(147,197,253,0.7)",
        },
      },
      {
        type: "DestinationGuide",
        props: {
          id: "DestinationGuide-3f8f00aa-9b3a-4c98-89ce-9f9123f85aa9",
          items: [
            {
              title: "Cala dei Pini",
              category: "Swim",
              distance: "8 min drive",
              imageAlt: "Cala dei Pini cove",
              imageUrl:
                "https://placehold.co/1200x900/cfd9d7/3f5853?text=Cala+dei+Pini",
              description:
                "A small cove framed by parasol pines. Quiet outside July and August.",
            },
            {
              title: "Trattoria del Borgo",
              category: "Eat",
              distance: "12 min walk",
              imageAlt: "Trattoria interior",
              imageUrl:
                "https://placehold.co/1200x900/efeae2/8b6f4e?text=Trattoria",
              description:
                "Family-run trattoria with handmade pasta and a garden out back. Book ahead.",
            },
            {
              title: "Sunset ridge trail",
              category: "Walk",
              distance: "From the door",
              imageAlt: "Ridge trail at sunset",
              imageUrl:
                "https://placehold.co/1200x900/c9d6c4/3f5340?text=Ridge+Trail",
              description:
                "A gentle one-hour loop with sweeping views over the valley and sea.",
            },
            {
              title: "Saturday market square",
              category: "Market",
              distance: "15 min drive",
              imageAlt: "Saturday market square",
              imageUrl:
                "https://placehold.co/1200x900/e7dccb/6a4b1f?text=Market",
              description:
                "Farmers, cheese, olive oil, and the best focaccia in the region. Mornings only.",
            },
          ],
          ctaUrl: "/local-guide",
          columns: "4",
          eyebrow: "EAT & DRINK",
          heading: "Tables we love",
          variant: "with-image",
          ctaLabel: "Read the full local guide",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          alignment: "left",
          textColor: "#0f172a",
          subheading:
            "Hand-picked places we'd send our own friends to — beaches, trails, restaurants, and the quiet corners.",
          accentColor: "#6b5b3e",
          backgroundColor: "#fafaf9",
          ctaOpenInNewTab: false,
        },
      },
      {
        type: "DestinationGuide",
        props: {
          id: "DestinationGuide-7c4c652f-63c6-4553-ab40-b2b4890b526e",
          items: [
            {
              title: "Cala dei Pini",
              category: "Swim",
              distance: "8 min drive",
              imageAlt: "Cala dei Pini cove",
              imageUrl:
                "https://placehold.co/1200x900/cfd9d7/3f5853?text=Cala+dei+Pini",
              description:
                "A small cove framed by parasol pines. Quiet outside July and August.",
            },
            {
              title: "Trattoria del Borgo",
              category: "Eat",
              distance: "12 min walk",
              imageAlt: "Trattoria interior",
              imageUrl:
                "https://placehold.co/1200x900/efeae2/8b6f4e?text=Trattoria",
              description:
                "Family-run trattoria with handmade pasta and a garden out back. Book ahead.",
            },
            {
              title: "Sunset ridge trail",
              category: "Walk",
              distance: "From the door",
              imageAlt: "Ridge trail at sunset",
              imageUrl:
                "https://placehold.co/1200x900/c9d6c4/3f5340?text=Ridge+Trail",
              description:
                "A gentle one-hour loop with sweeping views over the valley and sea.",
            },
            {
              title: "Saturday market square",
              category: "Market",
              distance: "15 min drive",
              imageAlt: "Saturday market square",
              imageUrl:
                "https://placehold.co/1200x900/e7dccb/6a4b1f?text=Market",
              description:
                "Farmers, cheese, olive oil, and the best focaccia in the region. Mornings only.",
            },
          ],
          ctaUrl: "/local-guide",
          columns: "4",
          eyebrow: "Outdoors",
          heading: "Trails, beaches, ridges",
          variant: "list",
          ctaLabel: "Read the full local guide",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          alignment: "left",
          textColor: "#0f172a",
          subheading:
            "Hand-picked places we'd send our own friends to — beaches, trails, restaurants, and the quiet corners.",
          accentColor: "#6b5b3e",
          backgroundColor: "#fafaf9",
          ctaOpenInNewTab: false,
        },
      },
      {
        type: "InquiryBand",
        props: {
          id: "InquiryBand-eeb11c2a-f6f9-4a51-8c20-9ab7994c41c1",
          eyebrow: "Plan your stay",
          heading: "Coming to stay? We'll help plan the rest.",
          variant: "calm",
          imageAlt: "Pine Hollow Lodge at dusk",
          imageUrl: "https://placehold.co/1600x900/efeae2/8b6f4e?text=Lodge",
          maxWidth: "xl",
          paddingX: "md",
          paddingY: "lg",
          textColor: "#0f172a",
          trustNote:
            "We reply within one business day · Real humans, no booking-engine bots",
          emailLabel: "Email",
          phoneLabel: "Call us",
          subheading:
            "Share your dates and a little about your trip. We respond within one business day, often the same morning.",
          accentColor: "#0f172a",
          phoneNumber: "+39 000 000 0000",
          ctaTextColor: "#ffffff",
          emailAddress: "stay@pinehollow.example",
          primaryCtaUrl: "/contact",
          showSecondary: true,
          whatsappLabel: "WhatsApp",
          whatsappNumber: "+39 000 000 0000",
          backgroundColor: "#f5f3ee",
          primaryCtaLabel: "Send an inquiry",
          primaryCtaOpenInNewTab: false,
        },
      },
      {
        type: "Footer",
        props: {
          id: "Footer-724c0927-4cb1-4719-a033-ab632d2d027b",
          columns: [
            {
              links: [
                {
                  href: "/rooms",
                  label: "Rooms & Suites",
                  openInNewTab: false,
                },
                {
                  href: "/cabins",
                  label: "Cabins & Villas",
                  openInNewTab: false,
                },
                {
                  href: "/offers",
                  label: "Offers & Packages",
                  openInNewTab: false,
                },
                {
                  href: "/gift-vouchers",
                  label: "Gift Vouchers",
                  openInNewTab: false,
                },
              ],
              heading: "Stay",
            },
            {
              links: [
                {
                  href: "/amenities",
                  label: "Amenities",
                  openInNewTab: false,
                },
                { href: "/gallery", label: "Gallery", openInNewTab: false },
                {
                  href: "/local-guide",
                  label: "Local Guide",
                  openInNewTab: false,
                },
                { href: "/about", label: "Our Story", openInNewTab: false },
              ],
              heading: "Explore",
            },
            {
              links: [
                {
                  href: "/contact",
                  label: "Send an Inquiry",
                  openInNewTab: false,
                },
                { href: "/faq", label: "FAQ", openInNewTab: false },
                {
                  href: "/policies",
                  label: "House Policies",
                  openInNewTab: false,
                },
                {
                  href: "/directions",
                  label: "Directions",
                  openInNewTab: false,
                },
              ],
              heading: "Plan your stay",
            },
          ],
          tagline:
            "A small, independently owned lodge. Hosted in person, written to by hand, and easy to reach for direct stays.",
          logoHref: "/",
          logoText: "Pine Hollow Lodge",
          logoType: "text",
          maxWidth: "xl",
          paddingX: "xl",
          paddingY: "sm",
          borderTop: false,
          textColor: "#000000",
          accentColor: "#171717",
          bottomBarBg: "#FFFFFF",
          bottomLinks: [
            { href: "/privacy", label: "Privacy", openInNewTab: false },
            { href: "/terms", label: "Terms", openInNewTab: false },
            { href: "/cookies", label: "Cookies", openInNewTab: false },
          ],
          linkOpacity: 70,
          socialLinks: [],
          dividerColor: "#FFFFFF",
          linkFontSize: 14,
          logoFontSize: 22,
          logoImageAlt: "Lodge logo",
          logoImageUrl: "",
          columnsLayout: "3",
          copyrightText: "© 2026 Pine Hollow Lodge. All rights reserved.",
          showBottomBar: true,
          socialIconGap: 12,
          borderTopColor: "",
          borderTopWidth: 1,
          linkHoverColor: "",
          logoFontWeight: "bold",
          logoImageWidth: 0,
          socialIconSize: 20,
          taglineOpacity: 80,
          backgroundColor: "#FFFFFF",
          logoColumnWidth: "50%",
          logoImageHeight: 38,
          taglineFontSize: 16,
          taglineMaxWidth: 380,
          bottomBarOpacity: 100,
          linkHoverOpacity: 100,
          bottomBarFontSize: 14,
          socialIconOpacity: 60,
          bottomBarTextColor: "#696969",
          bottomBarBorderColor: "#d4d4d4",
          columnHeadingFontSize: 14,
          logoImageBorderRadius: "none",
          columnHeadingTransform: "capitalize",
          columnHeadingLetterSpacing: "normal",
        },
      },
    ],
  },
};

// ─── Blank ─────────────────────────────────────────────────────────────────

export const BLANK_TEMPLATE: PuckTemplate = {
  id: "blank",
  label: "Blank page",
  description: "Start from an empty page and drag sections in yourself.",
  suggestedSlug: "",
  suggestedTitle: "",
  puckData: {
    root: { props: { title: "" } },
    content: [],
  },
};

// ─── Exported list ─────────────────────────────────────────────────────────

export const hospitalityTemplates: PuckTemplate[] = [
  boutiqueLodgeHome,
  villaRentalHome,
  accommodationDetail,
  contactInquiry,
  localGuide,
];

export const pageTemplates: PuckTemplate[] = [
  BLANK_TEMPLATE,
  ...hospitalityTemplates,
];

export function getPageTemplate(id: string): PuckTemplate | undefined {
  return pageTemplates.find((t) => t.id === id);
}
